/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useCallback } from "react";
import { TouchableOpacity, View, Platform } from "react-native";
import Icon from "react-icons-kit";
import ButtonBase from "@material-ui/core/ButtonBase";
import Fade from "@material-ui/core/Fade";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { Draggable } from "react-beautiful-dnd";
import { arrows_horizontal } from "react-icons-kit/ikons/arrows_horizontal";
import { trash } from "react-icons-kit/fa/trash";
import { clone } from "react-icons-kit/fa/clone";
import { arrow_down } from "react-icons-kit/ikons/arrow_down";

import { Button, Text, styles } from "UpduoLibrary";
import {
  skaleBlue,
  skaleBorderLightGray,
  skaleMainBlue,
  skaleRed,
  skalePrimaryBlue,
  skaleWebFillGray,
  skaleWebTextBackgroundLightGray,
  skaleWebTextGray,
  skaleWhite,
  skaleYellow,
} from "config/UpduoColors";
import SkillStepCard from "./SkillStepCard";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function SkillStepCardRow({
  cardKey,
  skillSessionStep,
  cardWidth,
  minute,
  onEditSessionSteps,
  onDelete,
  onClone,
  isEnabledClone,
  isEnabledDelete,
}) {
  const [anchorEl, setAnchorEl] = useState<Element | undefined>();
  const [isHoveringDelete, setIsHoveringDelete] = useState(false);
  const [isHoveringClone, setIsHoveringClone] = useState(false);
  const [isDeleteConfirmationShown, setIsDeleteConfirmationShown] =
    useState(false);

  const deleteBtnColor = isHoveringDelete ? skaleYellow : skaleMainBlue;
  const cloneBtnColor = isHoveringClone ? skaleYellow : skaleMainBlue;

  const _handleChange = useCallback(
    (minuteValue) => {
      const newSkillSessionStep = JSON.parse(JSON.stringify(skillSessionStep));
      newSkillSessionStep.minutes = minuteValue;
      onEditSessionSteps({
        cardKey,
        skillSessionStep: newSkillSessionStep,
      });
      setAnchorEl(undefined);
    },
    [skillSessionStep, cardKey, onEditSessionSteps]
  );

  const _handleEdit = useCallback(
    (isLearner, newSkillSessionStepRole) => {
      const newSkillSessionStep = JSON.parse(JSON.stringify(skillSessionStep));
      newSkillSessionStep[isLearner ? "learner" : "teacher"] = Object.assign(
        {},
        newSkillSessionStepRole
      );
      onEditSessionSteps({
        cardKey,
        skillSessionStep: newSkillSessionStep,
      });
    },
    [skillSessionStep, cardKey, onEditSessionSteps]
  );

  const _handleStepCardSwap = useCallback(() => {
    const newSkillSessionStep = JSON.parse(JSON.stringify(skillSessionStep));
    newSkillSessionStep.teacher = JSON.parse(
      JSON.stringify(skillSessionStep.learner)
    );
    newSkillSessionStep.learner = JSON.parse(
      JSON.stringify(skillSessionStep.teacher)
    );
    onEditSessionSteps({
      cardKey,
      skillSessionStep: newSkillSessionStep,
    });
  }, [cardKey, skillSessionStep]);

  const [isHoveringSwap, setIsHoveringSwap] = useState(false);

  const stepCardSwapButton = (
    <TouchableOpacity
      style={{
        height: 30,
        width: 30,
        justifyContent: "center",
        alignItems: "center",
      }}
      onPress={() => {
        UpduoAnalytics.logEvent("user-swap-step", {
          skillSessionStepTeacher: skillSessionStep.teacher,
          skillSessionStepLearner: skillSessionStep.learner,
          skillSessionStepIndex: skillSessionStep.index,
        });
        _handleStepCardSwap();
      }}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringSwap(true);
          },
          onMouseLeave: () => {
            setIsHoveringSwap(false);
          },
        },
      })}
    >
      <div
        style={{
          color: isHoveringSwap ? skaleYellow : skaleBlue,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={arrows_horizontal} />
      </div>
    </TouchableOpacity>
  );

  const stepCardDeleteButton = (
    <TouchableOpacity
      style={{
        height: 30,
        width: 30,
        justifyContent: "center",
        alignItems: "center",
      }}
      disabled={!isEnabledDelete}
      onPress={() => {
        UpduoAnalytics.logEvent("user-delete-step", {
          skillSessionStepIndex: skillSessionStep.index,
        });
        setIsDeleteConfirmationShown(true);
      }}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringDelete(true);
          },
          onMouseLeave: () => {
            setIsHoveringDelete(false);
          },
        },
      })}
    >
      <div
        style={{
          color: isEnabledDelete ? deleteBtnColor : skaleWebFillGray,
        }}
      >
        <Icon size={15} icon={trash} />
      </div>
    </TouchableOpacity>
  );

  const stepCardCloneButton = isEnabledClone ? (
    <TouchableOpacity
      style={{
        height: 30,
        width: 30,
        justifyContent: "center",
        alignItems: "center",
      }}
      onPress={() => {
        UpduoAnalytics.logEvent("user-clone-step", {
          skillSessionStepIndex: skillSessionStep.index,
        });
        onClone(cardKey);
      }}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringClone(true);
          },
          onMouseLeave: () => {
            setIsHoveringClone(false);
          },
        },
      })}
    >
      <div
        style={{
          color: cloneBtnColor,
        }}
      >
        <Icon size={15} icon={clone} />
      </div>
    </TouchableOpacity>
  ) : (
    <div
      style={{
        color: skaleWebFillGray,
        paddingBottom: 2.5,
        marginLeft: 8,
      }}
    >
      <Icon size={15} icon={clone} />
    </div>
  );

  const deleteConfirmation = (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <Text style={{ color: skaleWebTextGray, marginLeft: 30 }}>
        {"Are you sure you want to delete this lesson?"}
      </Text>
      <Button
        onPress={() => {
          UpduoAnalytics.logEvent("user-step-delete-cancel", {
            skillSessionStepTeacher: skillSessionStep.teacher,
            skillSessionStepLearner: skillSessionStep.learner,
            skillSessionStepIndex: skillSessionStep.index,
          });
          setIsDeleteConfirmationShown(false);
        }}
        width={80}
        marginLeft={10}
        text={"Keep"}
        height={28}
        borderWidth={1}
        borderColor={skalePrimaryBlue}
        color={skaleWhite}
        textColor={skalePrimaryBlue}
      />
      <Button
        onPress={() => {
          UpduoAnalytics.logEvent("user-step-delete-confirm", {
            skillSessionStepTeacher: skillSessionStep.teacher,
            skillSessionStepLearner: skillSessionStep.learner,
            skillSessionStepIndex: skillSessionStep.index,
          });
          onDelete(cardKey);
          setIsDeleteConfirmationShown(false);
        }}
        width={120}
        marginLeft={10}
        text={"Delete"}
        height={28}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
      />
    </View>
  );

  if (!skillSessionStep.id) {
    return null;
  }

  const _handleGenerateMinuteString = (minuteValue) => {
    return minuteValue > 1 ? " Minutes" : " Minute";
  };

  const minuteArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const minuteMenuItems = minuteArray.map((item, itemIndex) => (
    <MenuItem
      key={itemIndex}
      onClick={() => {
        _handleChange(item);
      }}
      style={{
        color: skaleWebTextGray,
        fontFamily: "ObjectSans-Regular",
        fontSize: 14,
      }}
    >
      {`${item}${_handleGenerateMinuteString(item)}`}
    </MenuItem>
  ));

  return (
    <Draggable
      draggableId={skillSessionStep.id}
      index={cardKey}
      key={skillSessionStep.id}
      type={"step"}
    >
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <View
            style={{
              borderRadius: 12,
              backgroundColor: skaleWebTextBackgroundLightGray,
              paddingLeft: 24,
              paddingRight: 24,
              marginBottom: 25,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Text
                style={{
                  marginTop: 24,
                  marginBottom: 20,
                  fontSize: 20,
                  fontWeight: "bold",
                  color: skaleWebTextGray,
                }}
              >
                {skillSessionStep.title}
              </Text>
              <View style={{ flexDirection: "row", marginTop: 21.67 }}>
                {stepCardCloneButton}
                {stepCardDeleteButton}
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                marginBottom: isDeleteConfirmationShown ? 10 : 0,
              }}
            >
              <SkillStepCard
                skillSessionStepRole={skillSessionStep.teacher}
                cardWidth={cardWidth}
                onEdit={(newSkillSessionStepRole) =>
                  _handleEdit(false, newSkillSessionStepRole)
                }
              />
              <View
                style={{
                  width: 24,
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1,
                }}
              >
                <View
                  style={{
                    padding: 5,
                    borderWidth: 1,
                    borderRadius: 20,
                    borderColor: skaleBorderLightGray,
                    backgroundColor: skaleWhite,
                  }}
                >
                  {stepCardSwapButton}
                </View>
              </View>
              <SkillStepCard
                skillSessionStepRole={skillSessionStep.learner}
                cardWidth={cardWidth}
                onEdit={(newSkillSessionStepRole) =>
                  _handleEdit(true, newSkillSessionStepRole)
                }
              />
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                marginTop: 20,
                marginBottom: 20,
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    color: skaleWebTextGray,
                    fontSize: 14,
                  }}
                >
                  {"Recommended Minutes:"}
                </Text>
              </View>
              <View
                style={[
                  styles.dropdown,
                  {
                    marginTop: 0,
                    marginLeft: 16,
                    marginRight: 0,
                    width: 180,
                    height: 42,
                    justifyContent: "center",
                    paddingLeft: 18,
                    paddingRight: 18.97,
                  },
                ]}
              >
                <ButtonBase
                  aria-controls={"fade-menu"}
                  aria-haspopup={"true"}
                  onClick={(event) => {
                    setAnchorEl(event.currentTarget);
                  }}
                  style={{
                    color: skaleWebTextGray,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Text
                      style={{
                        color: skaleWebTextGray,
                        marginTop: 2,
                        fontSize: 14,
                      }}
                    >
                      {minute + _handleGenerateMinuteString(minute)}
                    </Text>
                    <Icon size={15} icon={arrow_down} />
                  </View>
                </ButtonBase>
                <Menu
                  id={"fade-menu"}
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => {
                    setAnchorEl(undefined);
                  }}
                  TransitionComponent={Fade}
                  style={{ marginTop: 2 }}
                >
                  {minuteMenuItems}
                </Menu>
              </View>
            </View>

            {isDeleteConfirmationShown ? (
              <View
                style={{
                  marginBottom: 30,
                }}
              >
                {deleteConfirmation}
              </View>
            ) : null}
          </View>
        </div>
      )}
    </Draggable>
  );
}
